import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {Skeleton} from '../../../../components/common/skeleton';
import {AnalyticsScalarMetricsEnum} from '../../../../models/api/analyticsMetricsModel';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {HeatMapValue} from './reportHeatmapElement';

/*
 * Props.
 */

interface HeatMapTopItemsProps {
  topItems: HeatMapValue[];
  selectedMetric: AnalyticsScalarMetricsEnum | undefined;
  height: number | undefined;
  isLoading: boolean;
  onHeatMapValueClick: (metricClicked: AnalyticsScalarMetricsEnum, item: HeatMapValue) => void;
}

/*
 * Constants.
 */

// The height of each item - used to calculate how many we want to render
// 12px wrapper padding height + 12px button padding height
// + 21px for the time + 20px for the value
const TopItemHeight = 65;
const DefaultNumberItemsToRender = 4;
const MaxNumberItemsToRender = 5;

/*
 * Style.
 */

const StyledHeatMapTopItemsWrapper = styled.div<{$isLoading: boolean}>`
  padding: 0px 42px;
  margin-top: ${(p) => (p.$isLoading ? '-6px' : '48px')};
`;

const StyledTopTimesTitle = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.medium};
  padding: 14px 6px;
`;

// 12 px total height (6px padding x2)
const StyledTopItemWrapper = styled.div`
  padding: 6px 0px;
`;

// 12 px total height (6px padding x2)
const StyledChromelessButton = styled(ChromelessButton)`
  padding: 6px;
  border-radius: 6px;

  &:hover {
    background-color: ${(p) => p.theme.greys.shade10};
  }
`;

// 21px total height (19px line height + 2px margin)
const StyledItemTime = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.large};
  margin-bottom: 2px;
`;

// 20px total height
const StyledItemValue = styled.div`
  font-weight: ${fontWeights.normal};
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  line-height: 20px;
`;

/*
 * Component.
 */

export const HeatMapTopItems: FC<HeatMapTopItemsProps> = ({
  topItems,
  selectedMetric,
  height,
  isLoading,
  onHeatMapValueClick,
}) => {
  const numberItemsToRender = height
    ? Math.min(Math.floor(height / TopItemHeight), MaxNumberItemsToRender)
    : DefaultNumberItemsToRender;

  return (
    <StyledHeatMapTopItemsWrapper $isLoading={isLoading}>
      <StyledTopTimesTitle>
        <Trans id="heatmap-top-times-title">Top times</Trans>
      </StyledTopTimesTitle>
      <div>
        {isLoading
          ? renderLoadingState(numberItemsToRender)
          : renderTopItems(topItems, numberItemsToRender, selectedMetric, onHeatMapValueClick)}
      </div>
    </StyledHeatMapTopItemsWrapper>
  );
};

function renderTopItems(
  topItems: ReadonlyArray<HeatMapValue>,
  numberItemsToRender: number,
  selectedMetric: AnalyticsScalarMetricsEnum | undefined,
  onHeatMapValueClick: (metricClicked: AnalyticsScalarMetricsEnum, item: HeatMapValue) => void,
) {
  return topItems
    .slice(0, numberItemsToRender)
    .map((item: HeatMapValue) => selectedMetric && renderTopItem(item, selectedMetric, onHeatMapValueClick));
}

function renderTopItem(
  item: HeatMapValue,
  selectedMetric: AnalyticsScalarMetricsEnum,
  onHeatMapValueClick: (metricClicked: AnalyticsScalarMetricsEnum, item: HeatMapValue) => void,
) {
  const {label: metricLabel} = getMetricLabels(selectedMetric);

  return (
    <StyledTopItemWrapper key={`heatmap-top-item-${item.key}`}>
      <StyledChromelessButton onClick={() => onHeatMapValueClick(selectedMetric, item)}>
        <StyledItemTime>
          {item.dayFull}, {item.timePeriod}
        </StyledItemTime>
        <StyledItemValue>
          {item.value.toLocaleString()} {metricLabel && metricLabel.toLocaleLowerCase()}
        </StyledItemValue>
      </StyledChromelessButton>
    </StyledTopItemWrapper>
  );
}

function renderLoadingState(numberOfItems: number) {
  return [...Array(numberOfItems).keys()].map((key) => renderLoadingItem(key));
}

function renderLoadingItem(key: number) {
  return (
    <StyledTopItemWrapper key={`skeleton-top-item-${key}`}>
      <StyledChromelessButton>
        <StyledItemTime>
          <Skeleton width="75%" height="18px" />
        </StyledItemTime>
        <StyledItemValue>
          <Skeleton width="50%" height="14px" />
        </StyledItemValue>
      </StyledChromelessButton>
    </StyledTopItemWrapper>
  );
}
